<script setup lang="ts">
import KnowledgeDocumentUploadDialog from '@/components/dialogs/KnowledgeDocumentUploadDialog.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { formatDateToMonthShort } from '@core/utils/formatters'
import { getDateLocale } from '@core/utils/locale'

definePage({
  meta: {
    action: 'manage',
    subject: 'scenarios',
  },
})

const { t } = useI18n()
const router = useRouter()
const { locale } = useI18n({ useScope: 'global' })
const dateLocale = getDateLocale(locale.value)
const userData = useCookie<any>('userData')

const userTimeZone = computed(
  () => userData.value?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
)

// Feature flag check
const orgSettings = useCookie<any>('organizationSettings')
if (!(orgSettings.value?.enabledFeatures ?? []).includes('knowledge-base'))
  await router.replace('/games/scenarios/list')

// Snackbar
const isSnackbarVisible = ref(false)
const snackbarText = ref('')

// Fetch documents
const documents = ref<any[]>([])
const isLoading = ref(true)

const fetchDocuments = async () => {
  try {
    const { data } = await $api('/knowledge-base/')

    documents.value = data.data ?? data ?? []
  }
  catch {
    documents.value = []
  }
  finally {
    isLoading.value = false
  }
}

await fetchDocuments()

// Filters
const searchQuery = ref('')
const statusFilter = ref<string | null>(null)

const statusOptions = [
  { title: t('knowledge-base-page.status.processing'), value: 'processing' },
  { title: t('knowledge-base-page.status.ready'), value: 'ready' },
  { title: t('knowledge-base-page.status.failed'), value: 'failed' },
]

const processingStatuses = ['uploaded', 'analyzing', 'extracting', 'chunking', 'embedding']

const filteredDocuments = computed(() => {
  let result = documents.value

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()

    result = result.filter((d: any) =>
      d.title.toLowerCase().includes(query) || d.originalFilename.toLowerCase().includes(query),
    )
  }

  if (statusFilter.value) {
    if (statusFilter.value === 'processing')
      result = result.filter((d: any) => processingStatuses.includes(d.status))
    else
      result = result.filter((d: any) => d.status === statusFilter.value)
  }

  return result
})

// Table headers
const headers = [
  { title: t('knowledge-base-page.headers.title'), key: 'title', sortable: true },
  { title: t('knowledge-base-page.headers.type'), key: 'type', sortable: true, width: '80px' },
  { title: t('knowledge-base-page.headers.size'), key: 'fileSize', sortable: true, width: '100px' },
  { title: t('knowledge-base-page.headers.chunks'), key: 'chunksCount', sortable: true, width: '120px' },
  { title: t('knowledge-base-page.headers.status'), key: 'status', sortable: true, width: '150px' },
  { title: t('knowledge-base-page.headers.created'), key: 'createdAt', sortable: true, width: '150px' },
  { title: t('knowledge-base-page.headers.actions'), key: 'actions', sortable: false, width: '120px' },
]

// Helper functions
const getTypeIcon = (type: string): string => {
  const icons: Record<string, string> = {
    pdf: 'tabler-file-type-pdf',
    docx: 'tabler-file-type-docx',
    txt: 'tabler-file-type-txt',
    csv: 'tabler-file-type-csv',
    xlsx: 'tabler-file-type-xls',
  }

  return icons[type] ?? 'tabler-file'
}

const getStatusColor = (status: string): string => {
  if (processingStatuses.includes(status))
    return 'info'
  if (status === 'ready')
    return 'success'
  if (status === 'failed')
    return 'error'

  return 'secondary'
}

const getStatusLabel = (status: string): string => {
  if (processingStatuses.includes(status))
    return t('knowledge-base-page.status.processing')
  if (status === 'ready')
    return t('knowledge-base-page.status.ready')
  if (status === 'failed')
    return t('knowledge-base-page.status.failed')

  return status
}

// Backend stores a reason code instead of text when the org's AI provider can't run the knowledge base.
const getErrorLabel = (message: string): string =>
  message === 'provider_unsupported' ? t('knowledge-base-page.provider-unsupported') : message

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024)
    return `${bytes} B`
  if (bytes < 1024 * 1024)
    return `${(bytes / 1024).toFixed(1)} KB`

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const formatDate = (dateStr: string): string => {
  return formatDateToMonthShort(dateStr, true, dateLocale, userTimeZone.value)
}

// Upload dialog
const isUploadDialogVisible = ref(false)

const onUploadComplete = async () => {
  isUploadDialogVisible.value = false
  snackbarText.value = t('knowledge-base-page.upload-success')
  isSnackbarVisible.value = true
  await fetchDocuments()
}

// Download
const downloadDocument = async (item: any) => {
  try {
    const response = await fetch(`/api/knowledge-base/${item.id}/download`, {
      credentials: 'same-origin',
    })

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')

    a.href = url
    a.download = item.originalFilename ?? item.title
    a.click()
    URL.revokeObjectURL(url)
  }
  catch (error) {
    console.error('Download failed:', error)
  }
}

// Retry
const retryDocument = async (item: any) => {
  try {
    await $api(`/knowledge-base/${item.id}/retry`, { method: 'POST' })
    snackbarText.value = t('knowledge-base-page.retry-success')
    isSnackbarVisible.value = true
    await fetchDocuments()
  }
  catch (error) {
    console.error('Retry failed:', error)
  }
}

// Delete
const isDeleteDialogVisible = ref(false)
const itemToDelete = ref<any>(null)

const confirmDelete = (item: any) => {
  itemToDelete.value = item
  isDeleteDialogVisible.value = true
}

const deleteDocument = async () => {
  if (!itemToDelete.value)
    return

  try {
    await $api(`/knowledge-base/${itemToDelete.value.id}`, { method: 'DELETE' })
    snackbarText.value = t('knowledge-base-page.delete-success')
    isSnackbarVisible.value = true
    await fetchDocuments()
  }
  catch (error) {
    console.error('Delete failed:', error)
  }
}

// Real-time status updates via Echo
onMounted(() => {
  if (!window.Echo || !userData.value?.id)
    return

  window.Echo.private(`users.${userData.value.id}`)
    .listen('.knowledge-base.document.status_changed', async (event: any) => {
      const doc = documents.value.find((d: any) => d.id === event.document_id)

      if (doc) {
        doc.status = event.status
        if (event.chunks_count !== undefined)
          doc.chunksCount = event.chunks_count
        if (event.error_message)
          doc.errorMessage = event.error_message
      }
      else {
        await fetchDocuments()
      }
    })
})

onBeforeUnmount(() => {
  if (window.Echo && userData.value?.id) {
    window.Echo.private(`users.${userData.value.id}`)
      .stopListening('.knowledge-base.document.status_changed')
  }
})
</script>

<template>
  <VCard>
    <VCardText>
      <div class="d-flex align-center flex-wrap gap-4">
        <h5 class="text-h5">
          {{ t('knowledge-base-page.title') }}
        </h5>
        <VSpacer />
        <VBtn
          color="primary"
          prepend-icon="tabler-upload"
          @click="isUploadDialogVisible = true"
        >
          {{ t('knowledge-base-page.upload') }}
        </VBtn>
      </div>
    </VCardText>

    <VCardText>
      <VRow>
        <VCol
          cols="12"
          md="4"
        >
          <AppTextField
            v-model="searchQuery"
            :placeholder="t('knowledge-base-page.search-placeholder')"
            prepend-inner-icon="tabler-search"
            clearable
          />
        </VCol>
        <VCol
          cols="12"
          md="3"
        >
          <AppSelect
            v-model="statusFilter"
            :items="statusOptions"
            :placeholder="t('knowledge-base-page.filter-status')"
            clearable
          />
        </VCol>
      </VRow>
    </VCardText>

    <VDataTable
      :headers="headers"
      :items="filteredDocuments"
      :loading="isLoading"
      :items-per-page="25"
    >
      <template #item.title="{ item }">
        <div>
          <span class="font-weight-medium">{{ item.title }}</span>
          <div class="text-caption text-disabled">
            {{ item.originalFilename }}
          </div>
        </div>
      </template>

      <template #item.type="{ item }">
        <VIcon :icon="getTypeIcon(item.type)" />
      </template>

      <template #item.fileSize="{ item }">
        {{ formatFileSize(item.fileSize) }}
      </template>

      <template #item.status="{ item }">
        <VChip
          :color="getStatusColor(item.status)"
          size="small"
        >
          {{ getStatusLabel(item.status) }}
        </VChip>
        <VTooltip
          v-if="item.status === 'failed' && item.errorMessage"
          activator="parent"
          location="top"
        >
          {{ getErrorLabel(item.errorMessage) }}
        </VTooltip>
      </template>

      <template #item.createdAt="{ item }">
        {{ formatDate(item.createdAt) }}
      </template>

      <template #item.actions="{ item }">
        <div class="d-flex gap-1">
          <VBtn
            v-if="item.status === 'ready'"
            icon="tabler-download"
            size="small"
            variant="text"
            @click="downloadDocument(item)"
          />
          <VBtn
            v-if="item.status === 'failed'"
            icon="tabler-refresh"
            size="small"
            variant="text"
            color="warning"
            @click="retryDocument(item)"
          />
          <VBtn
            icon="tabler-trash"
            size="small"
            variant="text"
            color="error"
            @click="confirmDelete(item)"
          />
        </div>
      </template>

      <template #no-data>
        <div class="text-center pa-4 text-disabled">
          {{ t('knowledge-base-page.no-documents') }}
        </div>
      </template>
    </VDataTable>
  </VCard>

  <KnowledgeDocumentUploadDialog
    v-model:is-dialog-visible="isUploadDialogVisible"
    @submit="onUploadComplete"
  />

  <ConfirmDialog
    v-model:isDialogVisible="isDeleteDialogVisible"
    :confirmation-question="t('knowledge-base-page.confirm-delete')"
    cancel-msg=""
    :cancel-title="t('confirmation.cancel')"
    :confirm-msg="t('knowledge-base-page.delete-success')"
    :confirm-title="t('confirmation.success')"
    @confirm="deleteDocument"
  />

  <VSnackbar
    v-model="isSnackbarVisible"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="success"
  >
    {{ snackbarText }}
  </VSnackbar>
</template>
